import React, { useState } from 'react';
import { Home, FileText, TrendingUp, User, CheckCircle, AlertCircle } from 'lucide-react';
import HomePage from './pages/HomePage';
import BaseDetailPage from './pages/BaseDetailPage';
import CreateOrderPage from './pages/CreateOrderPage';
import DiscoverPage from './pages/DiscoverPage';
import OrderListPage from './pages/OrderListPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ProfilePage from './pages/ProfilePage';
import InvoiceRecordsPage from './pages/InvoiceRecordsPage';
import SupplierManagePage from './pages/SupplierManagePage';
import MaterialDetailPage from './pages/MaterialDetailPage';
import IdCardUploadPage from './pages/IdCardUploadPage';
import FaceVerifyPage from './pages/FaceVerifyPage';
import ConfirmOrderPage from './pages/ConfirmOrderPage';
import BankCardListPage from './pages/BankCardListPage';
import BankCardFormPage from './pages/BankCardFormPage';
import ContractReadPage from './pages/ContractReadPage';
import QRCodePage from './pages/QRCodePage';
import SupplierConfirmPage from './pages/SupplierConfirmPage';
import SupplierVerifyPage from './pages/SupplierVerifyPage';
import OrderDetailPrototype1 from './pages/OrderDetailPrototype1';
import OrderDetailPrototype2 from './pages/OrderDetailPrototype2';
import OrderDetailPrototype3 from './pages/OrderDetailPrototype3';
import OrderDetailPrototype4 from './pages/OrderDetailPrototype4';

/**
 * 废钢直通车 - 终端客户版 React 组件
 * 包含：首页、找货、下单、签约、订单跟踪、结算确认、个人中心
 */

// --- 模拟数据 (Mock Data) ---
// 基地 mock 图片：工业/建筑风格占位图（picsum 按 seed 固定）
const getBaseImageUrl = (id) => `https://picsum.photos/seed/base${id}/400/300`;

const MOCK_BASES = [
    { id: 1, name: "慈湖基地", dist: "20km", distNum: 20, cityId: 'mas', priceRange: "5,000-26,000 元/吨", businessDesc: "主营度钢铁采购、加工、仓储及配送服务", address: "安徽省马鞍山市慈湖高新区" },
    { id: 2, name: "马钢诚兴马鞍山基地", dist: "3.5km", distNum: 3.5, cityId: 'mas', priceRange: "2,500-2,800 元/吨", businessDesc: "主营重废、中废采购与加工", address: "安徽省马鞍山市雨山区" },
    { id: 3, name: "马钢诚兴台州基地", dist: "5km", distNum: 5, cityId: 'tz', priceRange: "2,400-2,600 元/吨", businessDesc: "主营重废、打包料采购", address: "浙江省台州市路桥区" },
    { id: 4, name: "长乐回收加工中心", dist: "8km", distNum: 8, cityId: 'fz', priceRange: "2,300-2,500 元/吨", businessDesc: "主营统料回收与加工", address: "福建省福州市长乐区" },
    { id: 5, name: "嘉兴废钢回收基地", dist: "12km", distNum: 12, cityId: 'jx', priceRange: "2,450-2,600 元/吨", businessDesc: "主营剪切料采购与仓储", address: "浙江省嘉兴市南湖区" },
    { id: 6, name: "雨山基地", dist: "8km", distNum: 8, cityId: 'mas', priceRange: "4,800-25,000 元/吨", businessDesc: "主营废钢采购、加工、仓配一体化", address: "安徽省马鞍山市雨山区" }
];

// 各城市到其他城市的模拟距离（公里）
const CITY_DISTANCES = {
    'mas': { 'mas': 0, 'tz': 280, 'fz': 420, 'jx': 250, 'hz': 180, 'nj': 100, 'hf': 150, 'sh': 300 },
    'tz': { 'mas': 280, 'tz': 0, 'fz': 280, 'jx': 180, 'hz': 200, 'nj': 320, 'hf': 400, 'sh': 280 },
    'fz': { 'mas': 420, 'tz': 280, 'fz': 0, 'jx': 450, 'hz': 500, 'nj': 550, 'hf': 600, 'sh': 600 },
    'jx': { 'mas': 250, 'tz': 180, 'fz': 450, 'jx': 0, 'hz': 80, 'nj': 200, 'hf': 350, 'sh': 100 },
    'hz': { 'mas': 180, 'tz': 200, 'fz': 500, 'jx': 80, 'hz': 0, 'nj': 180, 'hf': 300, 'sh': 150 },
    'nj': { 'mas': 100, 'tz': 320, 'fz': 550, 'jx': 200, 'hz': 180, 'nj': 0, 'hf': 150, 'sh': 250 },
    'hf': { 'mas': 150, 'tz': 400, 'fz': 600, 'jx': 350, 'hz': 300, 'nj': 150, 'hf': 0, 'sh': 400 },
    'sh': { 'mas': 300, 'tz': 280, 'fz': 600, 'jx': 100, 'hz': 150, 'nj': 250, 'hf': 400, 'sh': 0 },
};


const calculateOrderStatus = (order, deliveries) => {
  if (order.mode === 'other' && !order.supplierConfirmed) {
    return '待确认';
  }
  
  if (!deliveries || deliveries.length === 0) {
    return '进行中';
  }
  
  const statuses = deliveries.map(d => d.status);
  const allPaid = statuses.every(s => s === '已付款');
  
  if (allPaid) {
    // 敞口需求默认半年截止时间
    const deadline = order.deliveryDeadline || (
      order.createdAt ? 
      new Date(new Date(order.createdAt).setMonth(new Date(order.createdAt).getMonth() + 6)).toISOString().split('T')[0] : 
      null
    );
    const isPastDeadline = deadline && new Date(deadline) < new Date();
    // 送货量即为预估送货重量
    const isWeightReached = order.actualWeight && order.estimatedWeight && order.actualWeight >= order.estimatedWeight;
    
    if (isPastDeadline || isWeightReached) {
      return '已完成';
    }
  }
  
  return '进行中';
};

const calculateOrderSubStatus = (deliveries) => {
  if (!deliveries || deliveries.length === 0) {
    return null;
  }
  
  const statuses = deliveries.map(d => d.status);
  
  if (statuses.some(s => s === '待送货')) return '待送货';
  if (statuses.some(s => ['已到场', '已入库'].includes(s))) return '送货中';
  if (statuses.some(s => s === '待结算')) return '待结算';
  if (statuses.some(s => s === '已结算')) return '已结算';
  if (statuses.some(s => s === '已开票')) return '已开票';
  
  return null;
};

const calculateProgress = (deliveries) => {
  if (!deliveries || deliveries.length === 0) {
    return { total: 0, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 0, paid: 0 };
  }
  
  return {
    total: deliveries.length,
    waiting: deliveries.filter(d => d.status === '待送货').length,
    arrived: deliveries.filter(d => d.status === '已到场').length,
    stored: deliveries.filter(d => d.status === '已入库').length,
    needConfirm: deliveries.filter(d => d.status === '待结算').length,
    settled: deliveries.filter(d => d.status === '已结算').length,
    invoiced: deliveries.filter(d => d.status === '已开票').length,
    paid: deliveries.filter(d => d.status === '已付款').length,
  };
};

const CURRENT_USER_ID = 'user_001';

const MOCK_ORDERS = [
  {
    id: 7,
    orderNo: 'ORD20260222001',
    status: '待确认',
    mode: 'other',
    organizerId: 'user_002',
    baseName: '马钢诚兴马鞍山基地',
    baseAddress: '安徽省马鞍山市雨山区',
    material: '重型废钢 (HMS1)',
    estimatedWeight: 50,
    estimatedAmount: 132500,
    actualWeight: 0,
    actualAmount: 0,
    deliveryDeadline: '2026-08-22',
    supplierInfo: { id: 'user_001', name: '张三', phone: '138****8888', idCard: '340***********5678' },
    supplierConfirmed: false,
    createdAt: '2026-02-22 09:30',
    invoiced: false,
    progress: { total: 0, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 0, paid: 0 },
    deliveries: []
  },
  {
    id: 8,
    orderNo: 'ORD20260222002',
    status: '待确认',
    mode: 'other',
    organizerId: 'user_001',
    baseName: '慈湖基地',
    baseAddress: '安徽省马鞍山市慈湖高新区',
    material: '中型废钢 (HMS2)',
    estimatedWeight: 30,
    estimatedAmount: 77400,
    actualWeight: 0,
    actualAmount: 0,
    deliveryDeadline: '2026-08-22',
    supplierInfo: { id: 'user_002', name: '赵六', phone: '138****8888', idCard: '340***********1234' },
    supplierConfirmed: false,
    createdAt: '2026-02-22 10:00',
    invoiced: false,
    progress: { total: 0, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 0, paid: 0 },
    deliveries: []
  },
  {
    id: 1,
    orderNo: 'ORD20260219001',
    status: '进行中',
    mode: 'self',
    baseName: '马钢诚兴马鞍山基地',
    baseAddress: '安徽省马鞍山市雨山区',
    material: '重型废钢 (HMS1)',
    estimatedWeight: 60,
    estimatedAmount: 159000,
    actualWeight: 35.5,
    actualAmount: 94075,
    deliveryDeadline: '2026-02-22',
    demandType: 'closed',
    createdAt: '2026-02-19 08:30',
    invoiced: false,
    progress: { total: 6, waiting: 1, arrived: 1, stored: 0, needConfirm: 2, settled: 1, invoiced: 1, paid: 0 },
    deliveries: [
      {
        id: 'D001',
        deliveryNo: 'DEL20260219001',
        status: '已开票',
        weight: 10.5,
        unitPrice: 2650,
        amount: 27825,
        vehicleNo: '皖E12345',
        driverName: '张师傅',
        driverPhone: '138****1234',
        createdAt: '2026-02-19 09:00',
        timeline: [
          { 
            time: '02-19 09:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260219001.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E12345',
              deliveryDate: '2026-02-19',
              driverName: '张师傅',
              driverPhone: '138****1234'
            }
          },
          { 
            time: '02-19 10:30', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260219001.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E12345',
              arrivalTime: '2026-02-19 10:30',
              arrivalPhoto: 'https://picsum.photos/400/300?random=1'
            }
          },
          { 
            time: '02-19 12:00', 
            status: '已入库', 
            action: '入库过磅 10.5吨', 
            document: '入库单_DEL20260219001.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '中型废钢 (HMS2)',
              tareWeight: 8.5,
              grossWeight: 19.0,
              netWeight: 10.5,
              estimatedDryBasis: 9.8,
              deduction: 0.7,
              unitPrice: 2650,
              totalAmount: 27825,
              photos: ['https://picsum.photos/400/300?random=2', 'https://picsum.photos/400/300?random=3']
            }
          },
          { 
            time: '02-19 14:00', 
            status: '已结算', 
            action: '结算确认 ¥27,825', 
            document: '结算单_DEL20260219001.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-19',
              settlementWeight: 10.5,
              taxIncludedAmount: 27825,
              taxExcludedAmount: 26500
            }
          },
          { 
            time: '02-19 15:30', 
            status: '已开票', 
            action: '开票完成', 
            document: '电子发票_DEL20260219001.pdf',
            documentType: 'invoice',
            documentData: {
              sellerName: '马鞍山废钢回收有限公司',
              invoiceNo: 'FP2026021900001',
              invoiceAmount: 27825,
              invoiceDate: '2026-02-19',
              taxRate: '5%',
              invoiceFile: 'https://picsum.photos/400/500?random=4'
            }
          },
        ]
      },
      {
        id: 'D002',
        deliveryNo: 'DEL20260219002',
        status: '待结算',
        weight: 12.3,
        unitPrice: 2650,
        amount: 32595,
        vehicleNo: '皖E23456',
        driverName: '李师傅',
        driverPhone: '139****5678',
        createdAt: '2026-02-19 10:00',
        timeline: [
          { 
            time: '02-19 10:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260219002.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E23456',
              deliveryDate: '2026-02-19',
              driverName: '李师傅',
              driverPhone: '139****5678'
            }
          },
          { 
            time: '02-19 11:30', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260219002.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E23456',
              arrivalTime: '2026-02-19 11:30',
              arrivalPhoto: 'https://picsum.photos/400/300?random=5'
            }
          },
          { 
            time: '02-19 13:30', 
            status: '已入库', 
            action: '入库过磅 12.3吨', 
            document: '入库单_DEL20260219002.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '中型废钢 (HMS2)',
              tareWeight: 9.2,
              grossWeight: 21.5,
              netWeight: 12.3,
              estimatedDryBasis: 11.5,
              deduction: 0.8,
              unitPrice: 2650,
              totalAmount: 32595,
              photos: ['https://picsum.photos/400/300?random=6', 'https://picsum.photos/400/300?random=7']
            }
          },
          { 
            time: '02-19 15:00', 
            status: '待结算', 
            action: '结算单待确认', 
            document: '结算单_DEL20260219002.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-19',
              settlementWeight: 12.3,
              taxIncludedAmount: 32595,
              taxExcludedAmount: 31043
            }
          },
        ]
      },
      {
        id: 'D003',
        deliveryNo: 'DEL20260219003',
        status: '待结算',
        weight: 8.2,
        unitPrice: 2650,
        amount: 21730,
        vehicleNo: '皖E34567',
        driverName: '王师傅',
        driverPhone: '137****9012',
        createdAt: '2026-02-19 11:00',
        timeline: [
          { 
            time: '02-19 11:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260219003.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E34567',
              deliveryDate: '2026-02-19',
              driverName: '王师傅',
              driverPhone: '137****9012'
            }
          },
          { 
            time: '02-19 12:30', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260219003.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E34567',
              arrivalTime: '2026-02-19 12:30',
              arrivalPhoto: 'https://picsum.photos/400/300?random=20'
            }
          },
          { 
            time: '02-19 14:30', 
            status: '已入库', 
            action: '入库过磅 8.2吨', 
            document: '入库单_DEL20260219003.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 7.0,
              grossWeight: 15.2,
              netWeight: 8.2,
              estimatedDryBasis: 7.6,
              deduction: 0.6,
              unitPrice: 2650,
              totalAmount: 21730,
              photos: ['https://picsum.photos/400/300?random=21', 'https://picsum.photos/400/300?random=22']
            }
          },
          { 
            time: '02-19 16:00', 
            status: '待结算', 
            action: '结算单待确认', 
            document: '结算单_DEL20260219003.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-19',
              settlementWeight: 8.2,
              taxIncludedAmount: 21730,
              taxExcludedAmount: 20695
            }
          },
        ]
      },
      {
        id: 'D004',
        deliveryNo: 'DEL20260219004',
        status: '已结算',
        weight: 4.5,
        unitPrice: 2650,
        amount: 11925,
        vehicleNo: '皖E45678',
        driverName: '赵师傅',
        driverPhone: '136****3456',
        createdAt: '2026-02-19 13:00',
        timeline: [
          { 
            time: '02-19 13:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260219004.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E45678',
              deliveryDate: '2026-02-19',
              driverName: '赵师傅',
              driverPhone: '136****3456'
            }
          },
          { 
            time: '02-19 14:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260219004.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E45678',
              arrivalTime: '2026-02-19 14:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=23'
            }
          },
          { 
            time: '02-19 15:30', 
            status: '已入库', 
            action: '入库过磅 4.5吨', 
            document: '入库单_DEL20260219004.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 4.0,
              grossWeight: 8.5,
              netWeight: 4.5,
              estimatedDryBasis: 4.2,
              deduction: 0.3,
              unitPrice: 2650,
              totalAmount: 11925,
              photos: ['https://picsum.photos/400/300?random=24', 'https://picsum.photos/400/300?random=25']
            }
          },
          { 
            time: '02-19 17:00', 
            status: '已结算', 
            action: '结算确认 ¥11,925', 
            document: '结算单_DEL20260219004.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-19',
              settlementWeight: 4.5,
              taxIncludedAmount: 11925,
              taxExcludedAmount: 11357
            }
          },
        ]
      },
      {
        id: 'D005',
        deliveryNo: 'DEL20260219005',
        status: '已到场',
        weight: null,
        amount: null,
        vehicleNo: '皖E56789',
        driverName: '钱师傅',
        driverPhone: '135****7890',
        createdAt: '2026-02-19 15:00',
        timeline: [
          { 
            time: '02-19 15:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260219005.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E56789',
              deliveryDate: '2026-02-19',
              driverName: '钱师傅',
              driverPhone: '135****7890'
            }
          },
          { 
            time: '02-19 16:30', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260219005.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E56789',
              arrivalTime: '2026-02-19 16:30',
              arrivalPhoto: 'https://picsum.photos/400/300?random=26'
            }
          },
        ]
      },
      {
        id: 'D006',
        deliveryNo: 'DEL20260219006',
        status: '待送货',
        weight: null,
        amount: null,
        vehicleNo: null,
        driverName: null,
        driverPhone: null,
        createdAt: '2026-02-19 17:00',
        timeline: [
          { 
            time: '02-19 17:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260219006.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '待分配',
              deliveryDate: '待确认',
              driverName: '待分配',
              driverPhone: '待分配'
            }
          },
        ]
      },
    ]
  },
  {
    id: 2,
    orderNo: 'ORD20260218002',
    status: '进行中',
    mode: 'other',
    baseName: '慈湖基地',
    baseAddress: '安徽省马鞍山市慈湖高新区',
    material: '中型废钢 (HMS2)',
    estimatedWeight: 40,
    estimatedAmount: 103200,
    actualWeight: 28.5,
    actualAmount: 73530,
    deliveryDeadline: '2026-02-20',
    supplierInfo: { name: '张三', phone: '138****8888', idCard: '340***********1234' },
    supplierConfirmed: true,
    createdAt: '2026-02-18 14:20',
    invoiced: false,
    progress: { total: 4, waiting: 0, arrived: 1, stored: 0, needConfirm: 1, settled: 1, invoiced: 1, paid: 1 },
    deliveries: [
      {
        id: 'D007',
        deliveryNo: 'DEL20260218001',
        status: '已付款',
        weight: 10.0,
        unitPrice: 2580,
        amount: 25800,
        vehicleNo: '皖E67890',
        driverName: '孙师傅',
        driverPhone: '134****1234',
        createdAt: '2026-02-18 15:00',
        timeline: [
          { 
            time: '02-18 15:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260218001.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E67890',
              deliveryDate: '2026-02-18',
              driverName: '孙师傅',
              driverPhone: '134****1234'
            }
          },
          { 
            time: '02-18 16:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260218001.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E67890',
              arrivalTime: '2026-02-18 16:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=10'
            }
          },
          { 
            time: '02-18 17:30', 
            status: '已入库', 
            action: '入库过磅 10.0吨', 
            document: '入库单_DEL20260218001.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '中型废钢 (HMS2)',
              tareWeight: 8.0,
              grossWeight: 18.0,
              netWeight: 10.0,
              estimatedDryBasis: 9.3,
              deduction: 0.7,
              unitPrice: 2580,
              totalAmount: 25800,
              photos: ['https://picsum.photos/400/300?random=11', 'https://picsum.photos/400/300?random=12']
            }
          },
          { 
            time: '02-18 19:00', 
            status: '已结算', 
            action: '结算确认 ¥25,800', 
            document: '结算单_DEL20260218001.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-18',
              settlementWeight: 10.0,
              taxIncludedAmount: 25800,
              taxExcludedAmount: 24571
            }
          },
          { 
            time: '02-18 20:00', 
            status: '已开票', 
            action: '开票完成', 
            document: '电子发票_DEL20260218001.pdf',
            documentType: 'invoice',
            documentData: {
              sellerName: '马鞍山废钢回收有限公司',
              invoiceNo: 'FP2026021800001',
              invoiceAmount: 25800,
              invoiceDate: '2026-02-18',
              taxRate: '5%',
              invoiceFile: 'https://picsum.photos/400/500?random=13'
            }
          },
          { 
            time: '02-18 21:00', 
            status: '已付款', 
            action: '付款完成', 
            document: '付款凭证_DEL20260218001.pdf',
            documentType: 'payment',
            documentData: {
              receiverName: '张三',
              receiverAccount: '6222 **** **** 1234',
              paymentTime: '2026-02-18 21:00',
              paymentAmount: 25800
            }
          },
        ]
      },
      {
        id: 'D008',
        deliveryNo: 'DEL20260218002',
        status: '已结算',
        weight: 10.5,
        unitPrice: 2580,
        amount: 27090,
        vehicleNo: '皖E78901',
        driverName: '周师傅',
        driverPhone: '133****5678',
        createdAt: '2026-02-18 16:30',
        timeline: [
          { 
            time: '02-18 16:30', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260218002.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E78901',
              deliveryDate: '2026-02-18',
              driverName: '周师傅',
              driverPhone: '133****5678'
            }
          },
          { 
            time: '02-18 17:30', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260218002.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E78901',
              arrivalTime: '2026-02-18 17:30',
              arrivalPhoto: 'https://picsum.photos/400/300?random=30'
            }
          },
          { 
            time: '02-18 19:00', 
            status: '已入库', 
            action: '入库过磅 10.5吨', 
            document: '入库单_DEL20260218002.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '中型废钢 (HMS2)',
              tareWeight: 8.5,
              grossWeight: 19.0,
              netWeight: 10.5,
              estimatedDryBasis: 9.8,
              deduction: 0.7,
              unitPrice: 2580,
              totalAmount: 27090,
              photos: ['https://picsum.photos/400/300?random=31', 'https://picsum.photos/400/300?random=32']
            }
          },
          { 
            time: '02-18 20:30', 
            status: '已结算', 
            action: '结算确认 ¥27,090', 
            document: '结算单_DEL20260218002.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-18',
              settlementWeight: 10.5,
              taxIncludedAmount: 27090,
              taxExcludedAmount: 25800
            }
          },
        ]
      },
      {
        id: 'D009',
        deliveryNo: 'DEL20260218003',
        status: '待结算',
        weight: 8.0,
        unitPrice: 2580,
        amount: 20640,
        vehicleNo: '皖E89012',
        driverName: '吴师傅',
        driverPhone: '132****9012',
        createdAt: '2026-02-18 18:00',
        timeline: [
          { 
            time: '02-18 18:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260218003.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E89012',
              deliveryDate: '2026-02-18',
              driverName: '吴师傅',
              driverPhone: '132****9012'
            }
          },
          { 
            time: '02-18 19:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260218003.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E89012',
              arrivalTime: '2026-02-18 19:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=33'
            }
          },
          { 
            time: '02-18 20:30', 
            status: '已入库', 
            action: '入库过磅 8.0吨', 
            document: '入库单_DEL20260218003.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '中型废钢 (HMS2)',
              tareWeight: 6.5,
              grossWeight: 14.5,
              netWeight: 8.0,
              estimatedDryBasis: 7.5,
              deduction: 0.5,
              unitPrice: 2580,
              totalAmount: 20640,
              photos: ['https://picsum.photos/400/300?random=34', 'https://picsum.photos/400/300?random=35']
            }
          },
          { 
            time: '02-18 22:00', 
            status: '待结算', 
            action: '结算单待确认', 
            document: '结算单_DEL20260218003.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-18',
              settlementWeight: 8.0,
              taxIncludedAmount: 20640,
              taxExcludedAmount: 19657
            }
          },
        ]
      },
      {
        id: 'D010',
        deliveryNo: 'DEL20260218004',
        status: '已到场',
        weight: null,
        amount: null,
        vehicleNo: '皖E90123',
        driverName: '郑师傅',
        driverPhone: '131****0123',
        createdAt: '2026-02-18 20:00',
        timeline: [
          { 
            time: '02-18 20:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260218004.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E90123',
              deliveryDate: '2026-02-18',
              driverName: '郑师傅',
              driverPhone: '131****0123'
            }
          },
          { 
            time: '02-18 21:30', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260218004.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E90123',
              arrivalTime: '2026-02-18 21:30',
              arrivalPhoto: 'https://picsum.photos/400/300?random=36'
            }
          },
        ]
      },
    ]
  },
  {
    id: 3,
    orderNo: 'ORD20260217003',
    status: '进行中',
    mode: 'self',
    baseName: '马钢诚兴台州基地',
    baseAddress: '浙江省台州市路桥区',
    material: '一级破碎料',
    estimatedWeight: 45,
    estimatedAmount: 121500,
    actualWeight: 20.2,
    actualAmount: 54540,
    deliveryDeadline: '2026-02-21',
    createdAt: '2026-02-17 10:15',
    invoiced: false,
    progress: { total: 5, waiting: 1, arrived: 1, stored: 2, needConfirm: 0, settled: 1, invoiced: 0, paid: 0 },
    deliveries: [
      {
        id: 'D011',
        deliveryNo: 'DEL20260217001',
        status: '已结算',
        weight: 10.0,
        unitPrice: 2700,
        amount: 27000,
        vehicleNo: '浙J12345',
        driverName: '冯师傅',
        driverPhone: '137****1111',
        createdAt: '2026-02-17 11:00',
        timeline: [
          { 
            time: '02-17 11:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260217001.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '浙J12345',
              deliveryDate: '2026-02-17',
              driverName: '冯师傅',
              driverPhone: '137****1111'
            }
          },
          { 
            time: '02-17 12:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260217001.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '浙J12345',
              arrivalTime: '2026-02-17 12:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=40'
            }
          },
          { 
            time: '02-17 14:00', 
            status: '已入库', 
            action: '入库过磅 10.0吨', 
            document: '入库单_DEL20260217001.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '一级破碎料',
              tareWeight: 8.0,
              grossWeight: 18.0,
              netWeight: 10.0,
              estimatedDryBasis: 9.3,
              deduction: 0.7,
              unitPrice: 2700,
              totalAmount: 27000,
              photos: ['https://picsum.photos/400/300?random=41', 'https://picsum.photos/400/300?random=42']
            }
          },
          { 
            time: '02-17 16:00', 
            status: '已结算', 
            action: '结算确认 ¥27,000', 
            document: '结算单_DEL20260217001.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-17',
              settlementWeight: 10.0,
              taxIncludedAmount: 27000,
              taxExcludedAmount: 25714
            }
          },
        ]
      },
      {
        id: 'D012',
        deliveryNo: 'DEL20260217002',
        status: '已入库',
        weight: 5.2,
        unitPrice: 2700,
        amount: 14040,
        vehicleNo: '浙J23456',
        driverName: '陈师傅',
        driverPhone: '138****2222',
        createdAt: '2026-02-17 14:00',
        timeline: [
          { 
            time: '02-17 14:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260217002.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '浙J23456',
              deliveryDate: '2026-02-17',
              driverName: '陈师傅',
              driverPhone: '138****2222'
            }
          },
          { 
            time: '02-17 15:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260217002.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '浙J23456',
              arrivalTime: '2026-02-17 15:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=43'
            }
          },
          { 
            time: '02-17 17:00', 
            status: '已入库', 
            action: '入库过磅 5.2吨', 
            document: '入库单_DEL20260217002.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '一级破碎料',
              tareWeight: 4.2,
              grossWeight: 9.4,
              netWeight: 5.2,
              estimatedDryBasis: 4.9,
              deduction: 0.3,
              unitPrice: 2700,
              totalAmount: 14040,
              photos: ['https://picsum.photos/400/300?random=44', 'https://picsum.photos/400/300?random=45']
            }
          },
        ]
      },
      {
        id: 'D013',
        deliveryNo: 'DEL20260217003',
        status: '已入库',
        weight: 5.0,
        unitPrice: 2700,
        amount: 13500,
        vehicleNo: '浙J34567',
        driverName: '褚师傅',
        driverPhone: '139****3333',
        createdAt: '2026-02-17 16:00',
        timeline: [
          { 
            time: '02-17 16:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260217003.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '浙J34567',
              deliveryDate: '2026-02-17',
              driverName: '褚师傅',
              driverPhone: '139****3333'
            }
          },
          { 
            time: '02-17 17:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260217003.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '浙J34567',
              arrivalTime: '2026-02-17 17:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=46'
            }
          },
          { 
            time: '02-17 18:30', 
            status: '已入库', 
            action: '入库过磅 5.0吨', 
            document: '入库单_DEL20260217003.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '一级破碎料',
              tareWeight: 4.0,
              grossWeight: 9.0,
              netWeight: 5.0,
              estimatedDryBasis: 4.7,
              deduction: 0.3,
              unitPrice: 2700,
              totalAmount: 13500,
              photos: ['https://picsum.photos/400/300?random=47', 'https://picsum.photos/400/300?random=48']
            }
          },
        ]
      },
      {
        id: 'D014',
        deliveryNo: 'DEL20260217004',
        status: '已到场',
        weight: null,
        amount: null,
        vehicleNo: '浙J45678',
        driverName: '卫师傅',
        driverPhone: '136****4444',
        createdAt: '2026-02-17 18:00',
        timeline: [
          { 
            time: '02-17 18:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260217004.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '浙J45678',
              deliveryDate: '2026-02-17',
              driverName: '卫师傅',
              driverPhone: '136****4444'
            }
          },
          { 
            time: '02-17 19:30', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260217004.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '浙J45678',
              arrivalTime: '2026-02-17 19:30',
              arrivalPhoto: 'https://picsum.photos/400/300?random=49'
            }
          },
        ]
      },
      {
        id: 'D015',
        deliveryNo: 'DEL20260217005',
        status: '待送货',
        weight: null,
        amount: null,
        vehicleNo: null,
        driverName: null,
        driverPhone: null,
        createdAt: '2026-02-17 20:00',
        timeline: [
          { 
            time: '02-17 20:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260217005.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '待分配',
              deliveryDate: '待确认',
              driverName: '待分配',
              driverPhone: '待分配'
            }
          },
        ]
      },
    ]
  },
  {
    id: 4,
    orderNo: 'ORD20260216002',
    status: '进行中',
    mode: 'other',
    baseName: '嘉兴废钢回收基地',
    baseAddress: '浙江省嘉兴市南湖区',
    material: '中型废钢 (HMS2)',
    estimatedWeight: 35,
    estimatedAmount: 89250,
    actualWeight: 30.5,
    actualAmount: 77850,
    deliveryDeadline: '2026-02-19',
    supplierInfo: { name: '李四', phone: '139****6666', idCard: '330***********5678' },
    supplierConfirmed: true,
    createdAt: '2026-02-16 11:00',
    invoiced: false,
    progress: { total: 4, waiting: 0, arrived: 0, stored: 1, needConfirm: 0, settled: 2, invoiced: 1, paid: 1 },
    deliveries: [
      {
        id: 'D016',
        deliveryNo: 'DEL20260216001',
        status: '已付款',
        weight: 10.5,
        unitPrice: 2550,
        amount: 26775,
        vehicleNo: '浙F12345',
        driverName: '蒋师傅',
        driverPhone: '135****5555',
        createdAt: '2026-02-16 12:00',
        timeline: [
          { 
            time: '02-16 12:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260216001.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '浙F12345',
              deliveryDate: '2026-02-16',
              driverName: '蒋师傅',
              driverPhone: '135****5555'
            }
          },
          { 
            time: '02-16 13:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260216001.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '浙F12345',
              arrivalTime: '2026-02-16 13:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=50'
            }
          },
          { 
            time: '02-16 15:00', 
            status: '已入库', 
            action: '入库过磅 10.5吨', 
            document: '入库单_DEL20260216001.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '中型废钢 (HMS2)',
              tareWeight: 8.5,
              grossWeight: 19.0,
              netWeight: 10.5,
              estimatedDryBasis: 9.8,
              deduction: 0.7,
              unitPrice: 2550,
              totalAmount: 26775,
              photos: ['https://picsum.photos/400/300?random=51', 'https://picsum.photos/400/300?random=52']
            }
          },
          { 
            time: '02-16 17:00', 
            status: '已结算', 
            action: '结算确认 ¥26,775', 
            document: '结算单_DEL20260216001.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-16',
              settlementWeight: 10.5,
              taxIncludedAmount: 26775,
              taxExcludedAmount: 25500
            }
          },
          { 
            time: '02-16 18:00', 
            status: '已开票', 
            action: '开票完成', 
            document: '电子发票_DEL20260216001.pdf',
            documentType: 'invoice',
            documentData: {
              sellerName: '嘉兴废钢回收有限公司',
              invoiceNo: 'FP2026021600001',
              invoiceAmount: 26775,
              invoiceDate: '2026-02-16',
              taxRate: '5%',
              invoiceFile: 'https://picsum.photos/400/500?random=53'
            }
          },
          { 
            time: '02-16 19:00', 
            status: '已付款', 
            action: '付款完成', 
            document: '付款凭证_DEL20260216001.pdf',
            documentType: 'payment',
            documentData: {
              receiverName: '李四',
              receiverAccount: '6222 **** **** 5678',
              paymentTime: '2026-02-16 19:00',
              paymentAmount: 26775
            }
          },
        ]
      },
      {
        id: 'D017',
        deliveryNo: 'DEL20260216002',
        status: '已结算',
        weight: 10.0,
        unitPrice: 2550,
        amount: 25500,
        vehicleNo: '浙F23456',
        driverName: '沈师傅',
        driverPhone: '136****6666',
        createdAt: '2026-02-16 14:00',
        timeline: [
          { 
            time: '02-16 14:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260216002.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '浙F23456',
              deliveryDate: '2026-02-16',
              driverName: '沈师傅',
              driverPhone: '136****6666'
            }
          },
          { 
            time: '02-16 15:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260216002.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '浙F23456',
              arrivalTime: '2026-02-16 15:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=54'
            }
          },
          { 
            time: '02-16 17:00', 
            status: '已入库', 
            action: '入库过磅 10.0吨', 
            document: '入库单_DEL20260216002.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '中型废钢 (HMS2)',
              tareWeight: 8.0,
              grossWeight: 18.0,
              netWeight: 10.0,
              estimatedDryBasis: 9.3,
              deduction: 0.7,
              unitPrice: 2550,
              totalAmount: 25500,
              photos: ['https://picsum.photos/400/300?random=55', 'https://picsum.photos/400/300?random=56']
            }
          },
          { 
            time: '02-16 19:00', 
            status: '已结算', 
            action: '结算确认 ¥25,500', 
            document: '结算单_DEL20260216002.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-16',
              settlementWeight: 10.0,
              taxIncludedAmount: 25500,
              taxExcludedAmount: 24286
            }
          },
        ]
      },
      {
        id: 'D018',
        deliveryNo: 'DEL20260216003',
        status: '已结算',
        weight: 5.0,
        unitPrice: 2550,
        amount: 12750,
        vehicleNo: '浙F34567',
        driverName: '韩师傅',
        driverPhone: '137****7777',
        createdAt: '2026-02-16 16:00',
        timeline: [
          { 
            time: '02-16 16:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260216003.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '浙F34567',
              deliveryDate: '2026-02-16',
              driverName: '韩师傅',
              driverPhone: '137****7777'
            }
          },
          { 
            time: '02-16 17:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260216003.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '浙F34567',
              arrivalTime: '2026-02-16 17:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=57'
            }
          },
          { 
            time: '02-16 19:00', 
            status: '已入库', 
            action: '入库过磅 5.0吨', 
            document: '入库单_DEL20260216003.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '中型废钢 (HMS2)',
              tareWeight: 4.0,
              grossWeight: 9.0,
              netWeight: 5.0,
              estimatedDryBasis: 4.7,
              deduction: 0.3,
              unitPrice: 2550,
              totalAmount: 12750,
              photos: ['https://picsum.photos/400/300?random=58', 'https://picsum.photos/400/300?random=59']
            }
          },
          { 
            time: '02-16 21:00', 
            status: '已结算', 
            action: '结算确认 ¥12,750', 
            document: '结算单_DEL20260216003.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-16',
              settlementWeight: 5.0,
              taxIncludedAmount: 12750,
              taxExcludedAmount: 12143
            }
          },
        ]
      },
      {
        id: 'D019',
        deliveryNo: 'DEL20260216004',
        status: '已入库',
        weight: 5.0,
        unitPrice: 2550,
        amount: 12750,
        vehicleNo: '浙F45678',
        driverName: '杨师傅',
        driverPhone: '138****8888',
        createdAt: '2026-02-16 18:00',
        timeline: [
          { 
            time: '02-16 18:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260216004.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '浙F45678',
              deliveryDate: '2026-02-16',
              driverName: '杨师傅',
              driverPhone: '138****8888'
            }
          },
          { 
            time: '02-16 19:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260216004.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '浙F45678',
              arrivalTime: '2026-02-16 19:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=60'
            }
          },
          { 
            time: '02-16 21:00', 
            status: '已入库', 
            action: '入库过磅 5.0吨', 
            document: '入库单_DEL20260216004.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '中型废钢 (HMS2)',
              tareWeight: 4.0,
              grossWeight: 9.0,
              netWeight: 5.0,
              estimatedDryBasis: 4.7,
              deduction: 0.3,
              unitPrice: 2550,
              totalAmount: 12750,
              photos: ['https://picsum.photos/400/300?random=61', 'https://picsum.photos/400/300?random=62']
            }
          },
        ]
      },
    ]
  },
  {
    id: 5,
    orderNo: 'ORD20260215001',
    status: '进行中',
    mode: 'self',
    baseName: '长乐回收加工中心',
    baseAddress: '福建省福州市长乐区',
    material: '重型废钢 (HMS1)',
    estimatedWeight: 30,
    estimatedAmount: 79500,
    actualWeight: 28.5,
    actualAmount: 75525,
    deliveryDeadline: '2026-02-18',
    createdAt: '2026-02-15 08:45',
    invoiced: false,
    progress: { total: 3, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 3, invoiced: 0, paid: 0 },
    deliveries: [
      {
        id: 'D020',
        deliveryNo: 'DEL20260215001',
        status: '已结算',
        weight: 10.0,
        unitPrice: 2650,
        amount: 26500,
        vehicleNo: '闽A12345',
        driverName: '朱师傅',
        driverPhone: '135****9999',
        createdAt: '2026-02-15 10:00',
        timeline: [
          { 
            time: '02-15 10:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260215001.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '闽A12345',
              deliveryDate: '2026-02-15',
              driverName: '朱师傅',
              driverPhone: '135****9999'
            }
          },
          { 
            time: '02-15 11:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260215001.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '闽A12345',
              arrivalTime: '2026-02-15 11:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=63'
            }
          },
          { 
            time: '02-15 13:00', 
            status: '已入库', 
            action: '入库过磅 10.0吨', 
            document: '入库单_DEL20260215001.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 8.0,
              grossWeight: 18.0,
              netWeight: 10.0,
              estimatedDryBasis: 9.3,
              deduction: 0.7,
              unitPrice: 2650,
              totalAmount: 26500,
              photos: ['https://picsum.photos/400/300?random=64', 'https://picsum.photos/400/300?random=65']
            }
          },
          { 
            time: '02-15 15:00', 
            status: '已结算', 
            action: '结算确认 ¥26,500', 
            document: '结算单_DEL20260215001.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-15',
              settlementWeight: 10.0,
              taxIncludedAmount: 26500,
              taxExcludedAmount: 25238
            }
          },
        ]
      },
      {
        id: 'D021',
        deliveryNo: 'DEL20260215002',
        status: '已结算',
        weight: 10.5,
        unitPrice: 2650,
        amount: 27825,
        vehicleNo: '闽A23456',
        driverName: '秦师傅',
        driverPhone: '136****0000',
        createdAt: '2026-02-15 12:00',
        timeline: [
          { 
            time: '02-15 12:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260215002.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '闽A23456',
              deliveryDate: '2026-02-15',
              driverName: '秦师傅',
              driverPhone: '136****0000'
            }
          },
          { 
            time: '02-15 13:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260215002.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '闽A23456',
              arrivalTime: '2026-02-15 13:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=66'
            }
          },
          { 
            time: '02-15 15:00', 
            status: '已入库', 
            action: '入库过磅 10.5吨', 
            document: '入库单_DEL20260215002.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 8.5,
              grossWeight: 19.0,
              netWeight: 10.5,
              estimatedDryBasis: 9.8,
              deduction: 0.7,
              unitPrice: 2650,
              totalAmount: 27825,
              photos: ['https://picsum.photos/400/300?random=67', 'https://picsum.photos/400/300?random=68']
            }
          },
          { 
            time: '02-15 17:00', 
            status: '已结算', 
            action: '结算确认 ¥27,825', 
            document: '结算单_DEL20260215002.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-15',
              settlementWeight: 10.5,
              taxIncludedAmount: 27825,
              taxExcludedAmount: 26500
            }
          },
        ]
      },
      {
        id: 'D022',
        deliveryNo: 'DEL20260215003',
        status: '已结算',
        weight: 8.0,
        unitPrice: 2650,
        amount: 21200,
        vehicleNo: '闽A34567',
        driverName: '尤师傅',
        driverPhone: '137****1111',
        createdAt: '2026-02-15 14:00',
        timeline: [
          { 
            time: '02-15 14:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260215003.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '闽A34567',
              deliveryDate: '2026-02-15',
              driverName: '尤师傅',
              driverPhone: '137****1111'
            }
          },
          { 
            time: '02-15 15:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260215003.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '闽A34567',
              arrivalTime: '2026-02-15 15:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=69'
            }
          },
          { 
            time: '02-15 17:00', 
            status: '已入库', 
            action: '入库过磅 8.0吨', 
            document: '入库单_DEL20260215003.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 6.5,
              grossWeight: 14.5,
              netWeight: 8.0,
              estimatedDryBasis: 7.5,
              deduction: 0.5,
              unitPrice: 2650,
              totalAmount: 21200,
              photos: ['https://picsum.photos/400/300?random=70', 'https://picsum.photos/400/300?random=71']
            }
          },
          { 
            time: '02-15 19:00', 
            status: '已结算', 
            action: '结算确认 ¥21,200', 
            document: '结算单_DEL20260215003.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-15',
              settlementWeight: 8.0,
              taxIncludedAmount: 21200,
              taxExcludedAmount: 20190
            }
          },
        ]
      },
    ]
  },
  {
    id: 6,
    orderNo: 'ORD20260214001',
    status: '已完成',
    mode: 'self',
    baseName: '雨山基地',
    baseAddress: '安徽省马鞍山市雨山区',
    material: '重型废钢 (HMS1)',
    estimatedWeight: 55,
    estimatedAmount: 145750,
    actualWeight: 53.5,
    actualAmount: 141775,
    deliveryDeadline: '2026-02-16',
    createdAt: '2026-02-14 09:00',
    invoiced: true,
    progress: { total: 5, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 5, paid: 5 },
    deliveries: [
      {
        id: 'D023',
        deliveryNo: 'DEL20260214001',
        status: '已付款',
        weight: 11.0,
        unitPrice: 2650,
        amount: 29150,
        vehicleNo: '皖E88881',
        driverName: '许师傅',
        driverPhone: '138****2222',
        createdAt: '2026-02-14 10:00',
        timeline: [
          { 
            time: '02-14 10:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260214001.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E88881',
              deliveryDate: '2026-02-14',
              driverName: '许师傅',
              driverPhone: '138****2222'
            }
          },
          { 
            time: '02-14 11:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260214001.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E88881',
              arrivalTime: '2026-02-14 11:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=72'
            }
          },
          { 
            time: '02-14 13:00', 
            status: '已入库', 
            action: '入库过磅 11.0吨', 
            document: '入库单_DEL20260214001.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 8.5,
              grossWeight: 19.5,
              netWeight: 11.0,
              estimatedDryBasis: 10.3,
              deduction: 0.7,
              unitPrice: 2650,
              totalAmount: 29150,
              photos: ['https://picsum.photos/400/300?random=73', 'https://picsum.photos/400/300?random=74']
            }
          },
          { 
            time: '02-14 15:00', 
            status: '已结算', 
            action: '结算确认 ¥29,150', 
            document: '结算单_DEL20260214001.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-14',
              settlementWeight: 11.0,
              taxIncludedAmount: 29150,
              taxExcludedAmount: 27762
            }
          },
          { 
            time: '02-14 16:00', 
            status: '已开票', 
            action: '开票完成', 
            document: '电子发票_DEL20260214001.pdf',
            documentType: 'invoice',
            documentData: {
              sellerName: '马钢诚兴金属资源有限公司',
              invoiceNo: 'FP2026021400001',
              invoiceAmount: 29150,
              invoiceDate: '2026-02-14',
              taxRate: '5%',
              invoiceFile: 'https://picsum.photos/400/500?random=75'
            }
          },
          { 
            time: '02-14 17:00', 
            status: '已付款', 
            action: '付款完成', 
            document: '付款凭证_DEL20260214001.pdf',
            documentType: 'payment',
            documentData: {
              receiverName: '李四',
              receiverAccount: '6222 **** **** 5678',
              paymentTime: '2026-02-14 17:00',
              paymentAmount: 29150
            }
          },
        ]
      },
      {
        id: 'D024',
        deliveryNo: 'DEL20260214002',
        status: '已付款',
        weight: 10.5,
        unitPrice: 2650,
        amount: 27825,
        vehicleNo: '皖E88882',
        driverName: '何师傅',
        driverPhone: '139****3333',
        createdAt: '2026-02-14 12:00',
        timeline: [
          { 
            time: '02-14 12:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260214002.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E88882',
              deliveryDate: '2026-02-14',
              driverName: '何师傅',
              driverPhone: '139****3333'
            }
          },
          { 
            time: '02-14 13:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260214002.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E88882',
              arrivalTime: '2026-02-14 13:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=76'
            }
          },
          { 
            time: '02-14 15:00', 
            status: '已入库', 
            action: '入库过磅 10.5吨', 
            document: '入库单_DEL20260214002.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 8.0,
              grossWeight: 18.5,
              netWeight: 10.5,
              estimatedDryBasis: 9.8,
              deduction: 0.7,
              unitPrice: 2650,
              totalAmount: 27825,
              photos: ['https://picsum.photos/400/300?random=77', 'https://picsum.photos/400/300?random=78']
            }
          },
          { 
            time: '02-14 17:00', 
            status: '已结算', 
            action: '结算确认 ¥27,825', 
            document: '结算单_DEL20260214002.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-14',
              settlementWeight: 10.5,
              taxIncludedAmount: 27825,
              taxExcludedAmount: 26500
            }
          },
          { 
            time: '02-14 18:00', 
            status: '已开票', 
            action: '开票完成', 
            document: '电子发票_DEL20260214002.pdf',
            documentType: 'invoice',
            documentData: {
              sellerName: '马钢诚兴金属资源有限公司',
              invoiceNo: 'FP2026021400002',
              invoiceAmount: 27825,
              invoiceDate: '2026-02-14',
              taxRate: '5%',
              invoiceFile: 'https://picsum.photos/400/500?random=79'
            }
          },
          { 
            time: '02-14 19:00', 
            status: '已付款', 
            action: '付款完成', 
            document: '付款凭证_DEL20260214002.pdf',
            documentType: 'payment',
            documentData: {
              receiverName: '李四',
              receiverAccount: '6222 **** **** 5678',
              paymentTime: '2026-02-14 19:00',
              paymentAmount: 27825
            }
          },
        ]
      },
      {
        id: 'D025',
        deliveryNo: 'DEL20260214003',
        status: '已付款',
        weight: 10.0,
        unitPrice: 2650,
        amount: 26500,
        vehicleNo: '皖E88883',
        driverName: '吕师傅',
        driverPhone: '137****4444',
        createdAt: '2026-02-14 14:00',
        timeline: [
          { 
            time: '02-14 14:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260214003.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E88883',
              deliveryDate: '2026-02-14',
              driverName: '吕师傅',
              driverPhone: '137****4444'
            }
          },
          { 
            time: '02-14 15:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260214003.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E88883',
              arrivalTime: '2026-02-14 15:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=80'
            }
          },
          { 
            time: '02-14 17:00', 
            status: '已入库', 
            action: '入库过磅 10.0吨', 
            document: '入库单_DEL20260214003.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 8.0,
              grossWeight: 18.0,
              netWeight: 10.0,
              estimatedDryBasis: 9.3,
              deduction: 0.7,
              unitPrice: 2650,
              totalAmount: 26500,
              photos: ['https://picsum.photos/400/300?random=81', 'https://picsum.photos/400/300?random=82']
            }
          },
          { 
            time: '02-14 19:00', 
            status: '已结算', 
            action: '结算确认 ¥26,500', 
            document: '结算单_DEL20260214003.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-14',
              settlementWeight: 10.0,
              taxIncludedAmount: 26500,
              taxExcludedAmount: 25238
            }
          },
          { 
            time: '02-14 20:00', 
            status: '已开票', 
            action: '开票完成', 
            document: '电子发票_DEL20260214003.pdf',
            documentType: 'invoice',
            documentData: {
              sellerName: '马钢诚兴金属资源有限公司',
              invoiceNo: 'FP2026021400003',
              invoiceAmount: 26500,
              invoiceDate: '2026-02-14',
              taxRate: '5%',
              invoiceFile: 'https://picsum.photos/400/500?random=83'
            }
          },
          { 
            time: '02-14 21:00', 
            status: '已付款', 
            action: '付款完成', 
            document: '付款凭证_DEL20260214003.pdf',
            documentType: 'payment',
            documentData: {
              receiverName: '李四',
              receiverAccount: '6222 **** **** 5678',
              paymentTime: '2026-02-14 21:00',
              paymentAmount: 26500
            }
          },
        ]
      },
      {
        id: 'D026',
        deliveryNo: 'DEL20260214004',
        status: '已付款',
        weight: 12.0,
        unitPrice: 2650,
        amount: 31800,
        vehicleNo: '皖E88884',
        driverName: '施师傅',
        driverPhone: '136****5555',
        createdAt: '2026-02-14 16:00',
        timeline: [
          { 
            time: '02-14 16:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260214004.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E88884',
              deliveryDate: '2026-02-14',
              driverName: '施师傅',
              driverPhone: '136****5555'
            }
          },
          { 
            time: '02-14 17:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260214004.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E88884',
              arrivalTime: '2026-02-14 17:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=84'
            }
          },
          { 
            time: '02-14 19:00', 
            status: '已入库', 
            action: '入库过磅 12.0吨', 
            document: '入库单_DEL20260214004.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 9.5,
              grossWeight: 21.5,
              netWeight: 12.0,
              estimatedDryBasis: 11.2,
              deduction: 0.8,
              unitPrice: 2650,
              totalAmount: 31800,
              photos: ['https://picsum.photos/400/300?random=85', 'https://picsum.photos/400/300?random=86']
            }
          },
          { 
            time: '02-14 21:00', 
            status: '已结算', 
            action: '结算确认 ¥31,800', 
            document: '结算单_DEL20260214004.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-14',
              settlementWeight: 12.0,
              taxIncludedAmount: 31800,
              taxExcludedAmount: 30286
            }
          },
          { 
            time: '02-14 22:00', 
            status: '已开票', 
            action: '开票完成', 
            document: '电子发票_DEL20260214004.pdf',
            documentType: 'invoice',
            documentData: {
              sellerName: '马钢诚兴金属资源有限公司',
              invoiceNo: 'FP2026021400004',
              invoiceAmount: 31800,
              invoiceDate: '2026-02-14',
              taxRate: '5%',
              invoiceFile: 'https://picsum.photos/400/500?random=87'
            }
          },
          { 
            time: '02-14 23:00', 
            status: '已付款', 
            action: '付款完成', 
            document: '付款凭证_DEL20260214004.pdf',
            documentType: 'payment',
            documentData: {
              receiverName: '李四',
              receiverAccount: '6222 **** **** 5678',
              paymentTime: '2026-02-14 23:00',
              paymentAmount: 31800
            }
          },
        ]
      },
      {
        id: 'D027',
        deliveryNo: 'DEL20260214005',
        status: '已付款',
        weight: 10.0,
        unitPrice: 2650,
        amount: 26500,
        vehicleNo: '皖E88885',
        driverName: '张师傅',
        driverPhone: '135****6666',
        createdAt: '2026-02-14 18:00',
        timeline: [
          { 
            time: '02-14 18:00', 
            status: '待送货', 
            action: '创建预约单', 
            document: '预到货通知单_DEL20260214005.pdf',
            documentType: 'arrivalNotice',
            documentData: {
              vehicleNo: '皖E88885',
              deliveryDate: '2026-02-14',
              driverName: '张师傅',
              driverPhone: '135****6666'
            }
          },
          { 
            time: '02-14 19:00', 
            status: '已到场', 
            action: '车辆到场确认', 
            document: '收货单_DEL20260214005.pdf',
            documentType: 'receipt',
            documentData: {
              vehicleNo: '皖E88885',
              arrivalTime: '2026-02-14 19:00',
              arrivalPhoto: 'https://picsum.photos/400/300?random=88'
            }
          },
          { 
            time: '02-14 21:00', 
            status: '已入库', 
            action: '入库过磅 10.0吨', 
            document: '入库单_DEL20260214005.pdf',
            documentType: 'warehouse',
            documentData: {
              materialName: '重型废钢 (HMS1)',
              tareWeight: 8.0,
              grossWeight: 18.0,
              netWeight: 10.0,
              estimatedDryBasis: 9.3,
              deduction: 0.7,
              unitPrice: 2650,
              totalAmount: 26500,
              photos: ['https://picsum.photos/400/300?random=89', 'https://picsum.photos/400/300?random=90']
            }
          },
          { 
            time: '02-14 23:00', 
            status: '已结算', 
            action: '结算确认 ¥26,500', 
            document: '结算单_DEL20260214005.pdf',
            documentType: 'settlement',
            documentData: {
              settlementDate: '2026-02-14',
              settlementWeight: 10.0,
              taxIncludedAmount: 26500,
              taxExcludedAmount: 25238
            }
          },
          { 
            time: '02-15 00:00', 
            status: '已开票', 
            action: '开票完成', 
            document: '电子发票_DEL20260214005.pdf',
            documentType: 'invoice',
            documentData: {
              sellerName: '马钢诚兴金属资源有限公司',
              invoiceNo: 'FP2026021400005',
              invoiceAmount: 26500,
              invoiceDate: '2026-02-15',
              taxRate: '5%',
              invoiceFile: 'https://picsum.photos/400/500?random=91'
            }
          },
          { 
            time: '02-15 01:00', 
            status: '已付款', 
            action: '付款完成', 
            document: '付款凭证_DEL20260214005.pdf',
            documentType: 'payment',
            documentData: {
              receiverName: '李四',
              receiverAccount: '6222 **** **** 5678',
              paymentTime: '2026-02-15 01:00',
              paymentAmount: 26500
            }
          },
        ]
      },
    ]
  },
  {
    id: 9,
    orderNo: 'ORD20260213001',
    status: '进行中',
    mode: 'other',
    baseName: '宁波废钢加工基地',
    baseAddress: '浙江省宁波市北仑区',
    material: '统料废钢',
    estimatedWeight: 25,
    estimatedAmount: 62500,
    actualWeight: null,
    actualAmount: null,
    deliveryDeadline: '2026-02-18',
    supplierInfo: { name: '王五', phone: '137****9999', idCard: '330***********9999' },
    supplierConfirmed: true,
    createdAt: '2026-02-13 15:00',
    invoiced: false,
    progress: { total: 3, waiting: 3, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 0, paid: 0 },
    deliveries: [
      {
        id: 'D028',
        deliveryNo: 'DEL20260213001',
        status: '待送货',
        weight: null,
        amount: null,
        vehicleNo: null,
        driverName: null,
        driverPhone: null,
        createdAt: '2026-02-13 15:00',
        timeline: [
          { time: '02-13 15:00', status: '待送货', action: '创建预约单', document: '预到货通知单_DEL20260213001.pdf' },
        ]
      },
      {
        id: 'D029',
        deliveryNo: 'DEL20260213002',
        status: '待送货',
        weight: null,
        amount: null,
        vehicleNo: null,
        driverName: null,
        driverPhone: null,
        createdAt: '2026-02-13 15:00',
        timeline: [
          { time: '02-13 15:00', status: '待送货', action: '创建预约单', document: '预到货通知单_DEL20260213002.pdf' },
        ]
      },
      {
        id: 'D030',
        deliveryNo: 'DEL20260213003',
        status: '待送货',
        weight: null,
        amount: null,
        vehicleNo: null,
        driverName: null,
        driverPhone: null,
        createdAt: '2026-02-13 15:00',
        timeline: [
          { time: '02-13 15:00', status: '待送货', action: '创建预约单', document: '预到货通知单_DEL20260213003.pdf' },
        ]
      },
    ]
  },
  {
    id: 10,
    orderNo: 'ORD20260212001',
    status: '进行中',
    mode: 'self',
    baseName: '马钢诚兴马鞍山基地',
    baseAddress: '安徽省马鞍山市雨山区',
    material: '中型废钢 (HMS2)',
    estimatedWeight: 40,
    estimatedAmount: 103200,
    actualWeight: 38.5,
    actualAmount: 99330,
    deliveryDeadline: '2026-02-15',
    createdAt: '2026-02-12 08:00',
    invoiced: true,
    invoiceNo: 'INV20260215001',
    invoiceAmount: 99330,
    invoiceTime: '2026-02-15 10:30',
    progress: { total: 3, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 3, paid: 0 },
    deliveries: [
      {
        id: 'D031',
        deliveryNo: 'DEL20260212001',
        status: '已开票',
        weight: 13.0,
        unitPrice: 2580,
        amount: 33540,
        vehicleNo: '皖E11111',
        driverName: '曹师傅',
        driverPhone: '138****0001',
        createdAt: '2026-02-12 09:00',
        timeline: [
          { time: '02-12 09:00', status: '待送货', action: '创建预约单', document: '预到货通知单_DEL20260212001.pdf' },
          { time: '02-12 10:30', status: '已到场', action: '车辆到场确认', document: '收货单_DEL20260212001.pdf' },
          { time: '02-12 12:00', status: '已入库', action: '入库过磅 13.0吨', document: '入库单_DEL20260212001.pdf' },
          { time: '02-12 14:00', status: '已结算', action: '结算确认 ¥33,540', document: '结算单_DEL20260212001.pdf' },
          { time: '02-15 10:30', status: '已开票', action: '开票完成', document: '电子发票_DEL20260212001.pdf' },
        ]
      },
      {
        id: 'D032',
        deliveryNo: 'DEL20260212002',
        status: '已开票',
        weight: 12.5,
        unitPrice: 2580,
        amount: 32250,
        vehicleNo: '皖E22222',
        driverName: '袁师傅',
        driverPhone: '139****0002',
        createdAt: '2026-02-12 11:00',
        timeline: [
          { time: '02-12 11:00', status: '待送货', action: '创建预约单', document: '预到货通知单_DEL20260212002.pdf' },
          { time: '02-12 12:30', status: '已到场', action: '车辆到场确认', document: '收货单_DEL20260212002.pdf' },
          { time: '02-12 14:00', status: '已入库', action: '入库过磅 12.5吨', document: '入库单_DEL20260212002.pdf' },
          { time: '02-12 16:00', status: '已结算', action: '结算确认 ¥32,250', document: '结算单_DEL20260212002.pdf' },
          { time: '02-15 10:30', status: '已开票', action: '开票完成', document: '电子发票_DEL20260212002.pdf' },
        ]
      },
      {
        id: 'D033',
        deliveryNo: 'DEL20260212003',
        status: '已开票',
        weight: 13.0,
        unitPrice: 2580,
        amount: 33540,
        vehicleNo: '皖E33333',
        driverName: '邓师傅',
        driverPhone: '137****0003',
        createdAt: '2026-02-12 14:00',
        timeline: [
          { time: '02-12 14:00', status: '待送货', action: '创建预约单', document: '预到货通知单_DEL20260212003.pdf' },
          { time: '02-12 15:30', status: '已到场', action: '车辆到场确认', document: '收货单_DEL20260212003.pdf' },
          { time: '02-12 17:00', status: '已入库', action: '入库过磅 13.0吨', document: '入库单_DEL20260212003.pdf' },
          { time: '02-12 19:00', status: '已结算', action: '结算确认 ¥33,540', document: '结算单_DEL20260212003.pdf' },
          { time: '02-15 10:30', status: '已开票', action: '开票完成', document: '电子发票_DEL20260212003.pdf' },
        ]
      },
    ]
  },
];

const MOCK_PENDING_TASKS = [
  {
    id: 'PT001',
    deliveryNo: 'DEL20260219002',
    deliveryId: 'D002',
    baseName: '马钢诚兴马鞍山基地',
    weight: 12.3,
    unitPrice: 2650,
    amount: 32595,
    orderNo: 'ORD20260219001',
    orderId: 1,
  },
  {
    id: 'PT002',
    deliveryNo: 'DEL20260219003',
    deliveryId: 'D003',
    baseName: '马钢诚兴马鞍山基地',
    weight: 8.2,
    unitPrice: 2650,
    amount: 21730,
    orderNo: 'ORD20260219001',
    orderId: 1,
  },
  {
    id: 'PT003',
    deliveryNo: 'DEL20260218003',
    deliveryId: 'D009',
    baseName: '慈湖基地',
    weight: 8.0,
    unitPrice: 2580,
    amount: 20640,
    orderNo: 'ORD20260218002',
    orderId: 2,
  },
];

// --- 子组件 (Sub-Components) ---

// 底部导航项
const NavItem = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${
      active ? 'text-blue-600' : 'text-gray-400'
    }`}
  >
    <Icon size={24} strokeWidth={active ? 2.5 : 2} />
    <span className="text-[10px] font-medium">{label}</span>
  </button>
);

// --- 主组件 (Main App Component) ---

export default function App() {
    const [view, setView] = useState('home');
    const [selectedBase, setSelectedBase] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [selectedMode, setSelectedMode] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [pendingOrderData, setPendingOrderData] = useState(null);
    const [pendingSupplierOrderData, setPendingSupplierOrderData] = useState(null);
    const [idCardData, setIdCardData] = useState(null);
    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const [bankCards, setBankCards] = useState([
      {
        id: 1,
        bankName: '工商银行',
        cardNumber: '6222021234567890123',
        branch: '马鞍山分行',
        expiryDate: '12/28'
      },
      {
        id: 2,
        bankName: '建设银行',
        cardNumber: '6217001234567890123',
        branch: '雨山支行',
        expiryDate: '08/27'
      },
      {
        id: 3,
        bankName: '农业银行',
        cardNumber: '6228481234567890123',
        branch: '慈湖支行',
        expiryDate: '06/26'
      }
    ]);
    const [selectedCardId, setSelectedCardId] = useState(1);
    const [editingCard, setEditingCard] = useState(null);
    const [isFromProfile, setIsFromProfile] = useState(false);
    const [isFromProfileAuth, setIsFromProfileAuth] = useState(false);
    const [isFromConfirmOrder, setIsFromConfirmOrder] = useState(false);
    const [isFromSupplierConfirm, setIsFromSupplierConfirm] = useState(false);
    const [purchaseContractAgreed, setPurchaseContractAgreed] = useState(false);
    const [serviceContractAgreed, setServiceContractAgreed] = useState(false);
    const [supplierPurchaseContractAgreed, setSupplierPurchaseContractAgreed] = useState(false);
    const [supplierServiceContractAgreed, setSupplierServiceContractAgreed] = useState(false);
    const [reorderData, setReorderData] = useState(null);
    const [notification, setNotification] = useState(null);
    const [highlightDeliveryId, setHighlightDeliveryId] = useState(null);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [currentContractType, setCurrentContractType] = useState(null);
    const [hasFaceVerified, setHasFaceVerified] = useState(false);
    const [pendingFaceVerifyCallback, setPendingFaceVerifyCallback] = useState(null);

    const handleReorder = (order) => {
        const base = MOCK_BASES.find(b => b.name === order.baseName);
        setReorderData({
            base: base,
            material: order.material,
            weight: order.estimatedWeight?.toString() || '',
            mode: order.mode || 'self',
            supplierInfo: order.supplierInfo
        });
        setPurchaseContractAgreed(false);
        setServiceContractAgreed(false);
        navigateTo('createOrder');
    };

    const navigateTo = (newView, data = null, openSchedule = false) => {
        if (data) {
            if (newView === 'baseDetail') setSelectedBase(data);
            if (newView === 'orderDetail') {
                setSelectedOrder(data);
                setHighlightDeliveryId(null);
            }
            if (newView === 'materialDetail') setSelectedMaterial(data);
            if (newView === 'confirmOrder') setOrderData(data);
            if (newView === 'qrCode') setOrderData(data);
            if (newView === 'supplierConfirm') setOrderData(data);
        }
        setView(newView);
        if (openSchedule && newView === 'orderDetail') {
            setShowScheduleModal(true);
        }
    };
    
    const handleCreateDelivery = (orderId, deliveryData) => {
        const orderIndex = MOCK_ORDERS.findIndex(o => o.id === orderId);
        if (orderIndex === -1) return;
        
        const order = MOCK_ORDERS[orderIndex];
        const deliveryCount = (order.deliveries?.length || 0) + 1;
        const deliveryId = `D${Date.now()}`;
        const deliveryNo = `DEL${new Date().toISOString().slice(0, 10).replace(/-/g, '')}${String(deliveryCount).padStart(2, '0')}`;
        
        const newDelivery = {
            id: deliveryId,
            deliveryNo: deliveryNo,
            status: '待送货',
            weight: null,
            unitPrice: 2650,
            amount: null,
            vehicleNo: deliveryData.vehicleNo,
            driverName: deliveryData.driverName,
            driverPhone: deliveryData.driverPhone,
            deliveryDate: deliveryData.deliveryDate,
            createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
            timeline: [
                { 
                    time: new Date().toISOString().slice(5, 16).replace('T', ' '), 
                    status: '待送货', 
                    action: '创建预约单', 
                    document: `预到货通知单_${deliveryNo}.pdf` 
                }
            ]
        };
        
        const updatedOrder = {
            ...order,
            deliveries: [...(order.deliveries || []), newDelivery],
            progress: calculateProgress([...(order.deliveries || []), newDelivery])
        };
        updatedOrder.status = calculateOrderStatus(updatedOrder, updatedOrder.deliveries);
        updatedOrder.subStatus = calculateOrderSubStatus(updatedOrder.deliveries);
        
        MOCK_ORDERS[orderIndex] = updatedOrder;
        setSelectedOrder(updatedOrder);
        showToast(`预约发货成功，送货单号: ${deliveryNo}`);
    };

    const handleConfirmOrder = (order) => {
        // 将订单数据转换为确认页需要的格式
        const orderDataForConfirm = {
            id: order.id,
            orderNo: order.orderNo,
            base: { name: order.baseName, address: order.baseAddress },
            material: order.material,
            weight: order.estimatedWeight,
            supplierInfo: order.supplierInfo,
            demandType: order.demandType,
            deliveryDeadline: order.deliveryDeadline,
            estimatedAmount: order.estimatedAmount,
            isExistingOrder: true,
        };
        setOrderData(orderDataForConfirm);
        setView('supplierConfirm');
    };

    const showToast = (msg) => {
        setNotification(msg);
        setTimeout(() => setNotification(null), 3000);
    };

    return (
        <div className="max-w-md mx-auto bg-gray-50 h-screen flex flex-col relative overflow-hidden shadow-2xl">
            
            {/* 顶部通知 Toast */}
            {notification && (
                <div className="absolute top-4 left-4 right-4 bg-gray-800 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center animate-bounce">
                    <CheckCircle size={20} className="mr-2 text-green-400" />
                    <span className="text-sm font-medium">{notification}</span>
                </div>
            )}

            {/* 实名认证提示弹窗 */}
            {showAuthDialog && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl animate-slide-up">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                                    <AlertCircle size={24} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white">需要实名认证</h3>
                                    <p className="text-sm text-blue-100">为保障交易安全，请先完成实名认证</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-3 mb-6">
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                                        <CheckCircle size={12} className="text-blue-600" />
                                    </div>
                                    <div className="text-sm text-slate-600">
                                        上传身份证正反面照片
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                                        <CheckCircle size={12} className="text-blue-600" />
                                    </div>
                                    <div className="text-sm text-slate-600">
                                        系统自动识别身份信息
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <button
                                    onClick={() => {
                                        setShowAuthDialog(false);
                                        navigateTo('idCardUpload');
                                    }}
                                    className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
                                >
                                    立即认证
                                </button>
                                <button
                                    onClick={() => setShowAuthDialog(false)}
                                    className="w-full py-3 bg-slate-100 text-slate-600 rounded-xl font-bold text-base hover:bg-slate-200 transition-all active:scale-[0.98]"
                                >
                                    取消
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 主视图区域 */}
            <div className="flex-1 overflow-y-auto hide-scrollbar pb-16">
                {view === 'home' && (
                    <HomePage
                        bases={MOCK_BASES}
                        onBaseClick={(base) => navigateTo('baseDetail', base)}
                        getBaseImageUrl={getBaseImageUrl}
                        pendingTasks={MOCK_PENDING_TASKS}
                        pendingConfirmOrders={MOCK_ORDERS.filter(o => o.status === '待确认')}
                        cityDistances={CITY_DISTANCES}
                        onViewTaskDetail={(task) => {
                            const order = MOCK_ORDERS.find(o => o.id === task.orderId);
                            if (order) {
                                setSelectedOrder(order);
                                setHighlightDeliveryId(task.deliveryId);
                                setView('orderDetail');
                            }
                        }}
                        onViewPendingOrder={(order) => {
                            setSelectedOrder(order);
                            // 判断当前用户是否是实供人
                            const isSupplier = order?.supplierInfo?.id === CURRENT_USER_ID;
                            if (isSupplier) {
                                // 实供人直接进入确认页
                                const orderDataForConfirm = {
                                    id: order.id,
                                    orderNo: order.orderNo,
                                    base: { name: order.baseName, address: order.baseAddress },
                                    material: order.material,
                                    weight: order.estimatedWeight,
                                    supplierInfo: order.supplierInfo,
                                    demandType: order.demandType,
                                    deliveryDeadline: order.deliveryDeadline,
                                    estimatedAmount: order.estimatedAmount,
                                    isExistingOrder: true,
                                };
                                setOrderData(orderDataForConfirm);
                                setView('supplierConfirm');
                            } else {
                                // 组织者进入订单详情页
                                setView('orderDetail');
                            }
                        }}
                    />
                )}
                {view === 'discover' && <DiscoverPage />}
                {view === 'orders' && (
                    <OrderListPage
                        orders={MOCK_ORDERS}
                        onOrderClick={(order, openSchedule = false) => {
                            if (order.status === '待确认') {
                                // 判断当前用户是否是实供人
                                const isSupplier = order?.supplierInfo?.id === CURRENT_USER_ID;
                                setSelectedOrder(order);
                                if (isSupplier) {
                                    // 实供人直接进入确认页
                                    const orderDataForConfirm = {
                                        id: order.id,
                                        orderNo: order.orderNo,
                                        base: { name: order.baseName, address: order.baseAddress },
                                        material: order.material,
                                        weight: order.estimatedWeight,
                                        supplierInfo: order.supplierInfo,
                                        demandType: order.demandType,
                                        deliveryDeadline: order.deliveryDeadline,
                                        estimatedAmount: order.estimatedAmount,
                                        isExistingOrder: true,
                                    };
                                    setOrderData(orderDataForConfirm);
                                    setView('supplierConfirm');
                                } else {
                                    // 组织者进入订单详情页
                                    setView('orderDetail');
                                }
                            } else {
                                navigateTo('orderDetail', order, openSchedule);
                            }
                        }}
                        onPrototypeClick={(prototypeId) => setView(prototypeId)}
                    />
                )}
                {view === 'profile' && (
                    <ProfilePage
                        onManageBankCards={() => {
                            setIsFromProfile(true);
                            navigateTo('bankCardList');
                        }}
                        onRealNameAuth={() => {
                            setIsFromProfileAuth(true);
                            navigateTo('idCardUpload');
                        }}
                        onViewInvoiceRecords={() => navigateTo('invoiceRecords')}
                        onManageSuppliers={() => navigateTo('supplierManage')}
                    />
                )}
                {view === 'invoiceRecords' && (
                    <InvoiceRecordsPage onBack={() => setView('profile')} />
                )}
                {view === 'supplierManage' && (
                    <SupplierManagePage onBack={() => setView('profile')} />
                )}
                {view === 'baseDetail' && (
                    <BaseDetailPage
                        base={selectedBase}
                        onBack={() => setView('home')}
                        onSell={(material, mode) => {
                            setSelectedMaterial(material);
                            setSelectedMode(mode);
                            navigateTo('createOrder');
                        }}
                        getBaseImageUrl={getBaseImageUrl}
                        onViewMaterial={(material) => navigateTo('materialDetail', material)}
                    />
                )}
                {view === 'createOrder' && (
                    <CreateOrderPage
                        base={reorderData?.base || selectedBase}
                        material={reorderData?.material || selectedMaterial}
                        reorderData={reorderData}
                        initialMode={reorderData?.mode || selectedMode}
                        onBack={() => {
                            setView('baseDetail');
                            setSelectedMaterial(null);
                            setSelectedMode(null);
                            setPurchaseContractAgreed(false);
                            setServiceContractAgreed(false);
                            setSupplierPurchaseContractAgreed(false);
                            setSupplierServiceContractAgreed(false);
                            setCurrentContractType(null);
                            setReorderData(null);
                        }}
                        onSubmit={(data) => {
                            if (data.mode === 'other') {
                                navigateTo('qrCode', data);
                            } else {
                                if (!isAuthenticated) {
                                    setPendingOrderData(data);
                                    setShowAuthDialog(true);
                                } else {
                                    navigateTo('confirmOrder', data);
                                }
                            }
                        }}
                        onReadContract={() => {
                            setSelectedMode('other');
                            setIsFromSupplierConfirm(false);
                            setCurrentContractType('service');
                            navigateTo('organizerContractRead');
                        }}
                        serviceContractAgreed={serviceContractAgreed}
                    />
                )}
                {view === 'qrCode' && (
                    <QRCodePage
                        orderData={orderData}
                        onBack={() => {
                            setView('baseDetail');
                            setSelectedMaterial(null);
                            setSelectedMode(null);
                            setOrderData(null);
                        }}
                        onViewOrders={() => {
                            // 找到刚创建的订单（组织者帮别人卖的最新订单）
                            const createdOrder = MOCK_ORDERS.find(o => o.mode === 'other' && o.organizerId === CURRENT_USER_ID && o.supplierConfirmed);
                            setOrderData(null);
                            if (createdOrder) {
                                showToast("订单创建成功！");
                                setSelectedOrder(createdOrder);
                                setView('orderDetail');
                            } else {
                                showToast("订单创建成功！");
                                setView('orders');
                            }
                        }}
                        onSimulateScan={() => {
                            navigateTo('supplierVerify');
                        }}
                    />
                )}
                {view === 'supplierVerify' && (
                    <SupplierVerifyPage
                        orderData={orderData}
                        onBack={() => setView('home')}
                        onVerified={() => {
                            navigateTo('supplierConfirm');
                        }}
                    />
                )}
                {view === 'supplierConfirm' && (
                    <SupplierConfirmPage
                        orderData={orderData}
                        bankCards={bankCards}
                        selectedCardId={selectedCardId}
                        onBack={() => setView('orders')}
                        onSubmit={() => {
                            if (!isAuthenticated) {
                                setPendingSupplierOrderData(orderData);
                                setShowAuthDialog(true);
                            } else if (!hasFaceVerified) {
                                setPendingFaceVerifyCallback('supplierConfirm');
                                navigateTo('faceVerify');
                            } else if (orderData?.isExistingOrder) {
                                // 确认已存在的订单：更新订单状态
                                const orderIndex = MOCK_ORDERS.findIndex(o => o.id === orderData.id);
                                if (orderIndex !== -1) {
                                    MOCK_ORDERS[orderIndex] = {
                                        ...MOCK_ORDERS[orderIndex],
                                        status: '进行中',
                                        supplierConfirmed: true,
                                        subStatus: '待送货',
                                    };
                                    const updatedOrder = MOCK_ORDERS[orderIndex];
                                    setSelectedOrder(updatedOrder);
                                    showToast("订单确认成功！可以开始预约送货了");
                                    setView('orderDetail');
                                }
                            } else {
                                // 创建新订单
                                const newOrderId = Date.now();
                                const newOrderNo = `ORD${new Date().toISOString().slice(0, 10).replace(/-/g, '')}${String(MOCK_ORDERS.length + 1).padStart(3, '0')}`;
                                const estimatedAmount = orderData?.weight ? parseFloat(orderData.weight) * 2650 : 0;

                                const newOrder = {
                                    id: newOrderId,
                                    orderNo: newOrderNo,
                                    status: '进行中',
                                    mode: 'other',
                                    organizerId: CURRENT_USER_ID,
                                    baseName: orderData?.base?.name || '马钢诚兴马鞍山基地',
                                    baseAddress: orderData?.base?.address || '安徽省马鞍山市雨山区',
                                    material: orderData?.material || '重型废钢 (HMS1)',
                                    estimatedWeight: parseFloat(orderData?.weight) || 0,
                                    estimatedAmount: estimatedAmount,
                                    actualWeight: 0,
                                    actualAmount: 0,
                                    deliveryDeadline: orderData?.deliveryDeadline || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
                                    supplierInfo: {
                                        id: 'supplier_001',
                                        name: orderData?.supplierInfo?.name || '实供人',
                                        phone: orderData?.supplierInfo?.phone || '',
                                        idCard: ''
                                    },
                                    supplierConfirmed: true,
                                    createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
                                    invoiced: false,
                                    progress: { total: 0, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 0, paid: 0 },
                                    deliveries: []
                                };

                                // 添加到订单列表
                                MOCK_ORDERS.unshift(newOrder);

                                showToast("订单确认成功！合同已自动生成");
                                setSelectedOrder(newOrder);
                                setView('orderDetail');
                            }
                        }}
                        onReadContract={() => {
                            setIsFromSupplierConfirm(true);
                            navigateTo('supplierContractRead');
                        }}
                        purchaseContractAgreed={supplierPurchaseContractAgreed}
                        serviceContractAgreed={supplierServiceContractAgreed}
                        onSelectBankCard={() => navigateTo('supplierBankCardList')}
                        onAddBankCard={() => {
                            setEditingCard(null);
                            setIsFromProfile(false);
                            navigateTo('supplierBankCardForm');
                        }}
                        isAuthenticated={isAuthenticated}
                    />
                )}
                {view === 'supplierBankCardList' && (
                    <BankCardListPage
                        bankCards={bankCards}
                        selectedCardId={selectedCardId}
                        onBack={() => setView('supplierConfirm')}
                        onSelect={(cardId) => {
                            setSelectedCardId(cardId);
                            setView('supplierConfirm');
                        }}
                        onAddNew={() => {
                            setEditingCard(null);
                            setIsFromProfile(false);
                            navigateTo('supplierBankCardForm');
                        }}
                        onEdit={(card) => {
                            setEditingCard(card);
                            setIsFromProfile(false);
                            navigateTo('supplierBankCardForm');
                        }}
                        onDelete={(cardId) => {
                            setBankCards(prev => prev.filter(card => card.id !== cardId));
                            if (selectedCardId === cardId) {
                                setSelectedCardId(null);
                            }
                            showToast("银行卡已删除");
                        }}
                    />
                )}
                {view === 'supplierBankCardForm' && (
                    <BankCardFormPage
                        editingCard={editingCard}
                        onBack={() => {
                            setView('supplierBankCardList');
                        }}
                        onSave={(card) => {
                            if (editingCard) {
                                setBankCards(prev => prev.map(c => c.id === card.id ? card : c));
                                showToast("银行卡已更新");
                            } else {
                                setBankCards(prev => [...prev, { ...card, id: Date.now() }]);
                                showToast("银行卡已添加");
                            }
                            setView('supplierBankCardList');
                        }}
                    />
                )}
                {view === 'orderDetail' && (
                    <OrderDetailPage
                        order={selectedOrder}
                        onBack={() => {
                            setShowScheduleModal(false);
                            setView('orders');
                        }}
                        onReorder={() => handleReorder(selectedOrder)}
                        highlightDeliveryId={highlightDeliveryId}
                        showScheduleModal={showScheduleModal}
                        onCloseScheduleModal={() => setShowScheduleModal(false)}
                        onCreateDelivery={handleCreateDelivery}
                        currentUserId={CURRENT_USER_ID}
                        onConfirmOrder={handleConfirmOrder}
                    />
                )}
                {view === 'prototype1' && (
                    <OrderDetailPrototype1 onBack={() => setView('orders')} />
                )}
                {view === 'prototype2' && (
                    <OrderDetailPrototype2 onBack={() => setView('orders')} />
                )}
                {view === 'prototype3' && (
                    <OrderDetailPrototype3 
                        onBack={() => setView('orders')} 
                        highlightDeliveryId={selectedOrder ? MOCK_PENDING_TASKS.find(t => t.orderId === selectedOrder.id)?.deliveryId : null}
                        onConfirmSettlement={(deliveryId) => showToast(`结算单已确认`)}
                        onRejectSettlement={(deliveryId, reason) => showToast(`结算单已驳回: ${reason}`)}
                    />
                )}
                {view === 'prototype4' && (
                    <OrderDetailPrototype4 onBack={() => setView('orders')} />
                )}
                {view === 'materialDetail' && (
                    <MaterialDetailPage
                        material={selectedMaterial}
                        base={selectedBase}
                        onBack={() => setView('baseDetail')}
                        onSell={(material, mode) => {
                            setSelectedMaterial(material);
                            setSelectedMode(mode);
                            navigateTo('createOrder');
                        }}
                    />
                )}
                {view === 'idCardUpload' && (
                    <IdCardUploadPage
                        onBack={() => {
                            if (isFromProfileAuth) {
                                setView('profile');
                                setIsFromProfileAuth(false);
                            } else if (pendingSupplierOrderData) {
                                setView('supplierConfirm');
                                setPendingSupplierOrderData(null);
                            } else {
                                setView('createOrder');
                                setPendingOrderData(null);
                            }
                        }}
                        onComplete={(data) => {
                            setIdCardData(data);
                            setIsAuthenticated(true);
                            showToast("实名认证成功！");
                            if (pendingOrderData) {
                                navigateTo('confirmOrder', pendingOrderData);
                                setPendingOrderData(null);
                            } else if (pendingSupplierOrderData) {
                                showToast("订单确认成功！合同已自动生成");
                                navigateTo('orders');
                                setPendingSupplierOrderData(null);
                            } else if (isFromProfileAuth) {
                                setView('profile');
                                setIsFromProfileAuth(false);
                            } else {
                                setView('createOrder');
                            }
                        }}
                    />
                )}
                {view === 'faceVerify' && (
                    <FaceVerifyPage
                        idCardData={idCardData}
                        onBack={() => {
                            if (pendingFaceVerifyCallback === 'confirmOrder') {
                                setView('confirmOrder');
                            } else if (pendingFaceVerifyCallback === 'supplierConfirm') {
                                setView('supplierConfirm');
                            } else {
                                navigateTo('idCardUpload');
                            }
                            setPendingFaceVerifyCallback(null);
                        }}
                        onSubmit={() => {
                            if (pendingFaceVerifyCallback === 'confirmOrder') {
                                setHasFaceVerified(true);
                                setPendingFaceVerifyCallback(null);

                                // 创建新订单（自己卖模式）
                                const newOrderId = Date.now();
                                const newOrderNo = `ORD${new Date().toISOString().slice(0, 10).replace(/-/g, '')}${String(MOCK_ORDERS.length + 1).padStart(3, '0')}`;
                                const estimatedAmount = orderData?.weight ? parseFloat(orderData.weight) * 2650 : 0;

                                const newOrder = {
                                    id: newOrderId,
                                    orderNo: newOrderNo,
                                    status: '进行中',
                                    mode: orderData?.mode || 'self',
                                    baseName: orderData?.base?.name || '马钢诚兴马鞍山基地',
                                    baseAddress: orderData?.base?.address || '安徽省马鞍山市雨山区',
                                    material: orderData?.material || '重型废钢 (HMS1)',
                                    estimatedWeight: parseFloat(orderData?.weight) || 0,
                                    estimatedAmount: estimatedAmount,
                                    actualWeight: 0,
                                    actualAmount: 0,
                                    deliveryDeadline: orderData?.deliveryDeadline || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
                                    supplierInfo: null,
                                    supplierConfirmed: true,
                                    createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
                                    invoiced: false,
                                    progress: { total: 0, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 0, paid: 0 },
                                    deliveries: []
                                };

                                MOCK_ORDERS.unshift(newOrder);

                                showToast("人脸识别成功！");
                                showToast("订单创建成功！合同已自动生成");
                                setSelectedOrder(newOrder);
                                setOrderData(null);
                                setView('orderDetail');
                            } else if (pendingFaceVerifyCallback === 'supplierConfirm') {
                                setHasFaceVerified(true);
                                setPendingFaceVerifyCallback(null);

                                // 创建新订单（实供人确认模式）
                                const newOrderId = Date.now();
                                const newOrderNo = `ORD${new Date().toISOString().slice(0, 10).replace(/-/g, '')}${String(MOCK_ORDERS.length + 1).padStart(3, '0')}`;
                                const estimatedAmount = orderData?.weight ? parseFloat(orderData.weight) * 2650 : 0;

                                const newOrder = {
                                    id: newOrderId,
                                    orderNo: newOrderNo,
                                    status: '进行中',
                                    mode: 'other',
                                    organizerId: CURRENT_USER_ID,
                                    baseName: orderData?.base?.name || '马钢诚兴马鞍山基地',
                                    baseAddress: orderData?.base?.address || '安徽省马鞍山市雨山区',
                                    material: orderData?.material || '重型废钢 (HMS1)',
                                    estimatedWeight: parseFloat(orderData?.weight) || 0,
                                    estimatedAmount: estimatedAmount,
                                    actualWeight: 0,
                                    actualAmount: 0,
                                    deliveryDeadline: orderData?.deliveryDeadline || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
                                    supplierInfo: {
                                        id: 'supplier_001',
                                        name: orderData?.supplierInfo?.name || '实供人',
                                        phone: orderData?.supplierInfo?.phone || '',
                                        idCard: ''
                                    },
                                    supplierConfirmed: true,
                                    createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
                                    invoiced: false,
                                    progress: { total: 0, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 0, paid: 0 },
                                    deliveries: []
                                };

                                MOCK_ORDERS.unshift(newOrder);

                                showToast("人脸识别成功！");
                                showToast("订单确认成功！合同已自动生成");
                                setSelectedOrder(newOrder);
                                setOrderData(null);
                                setView('orderDetail');
                            } else if (pendingOrderData) {
                                setIsAuthenticated(true);
                                showToast("实名认证成功！");
                                navigateTo('confirmOrder', pendingOrderData);
                                setPendingOrderData(null);
                            } else if (pendingSupplierOrderData) {
                                setIsAuthenticated(true);
                                showToast("实名认证成功！");
                                showToast("订单确认成功！合同已自动生成");
                                navigateTo('orders');
                                setPendingSupplierOrderData(null);
                            } else if (isFromProfileAuth) {
                                setIsAuthenticated(true);
                                showToast("实名认证成功！");
                                setView('profile');
                                setIsFromProfileAuth(false);
                            } else {
                                setIsAuthenticated(true);
                                showToast("实名认证成功！");
                                setView('createOrder');
                            }
                        }}
                    />
                )}
                {view === 'confirmOrder' && (
                    <ConfirmOrderPage
                        orderData={orderData}
                        bankCards={bankCards}
                        selectedCardId={selectedCardId}
                        onBack={() => setView('createOrder')}
                        onSelectBankCard={() => {
                            setIsFromProfile(false);
                            navigateTo('bankCardList');
                        }}
                        onAddBankCard={() => {
                            setEditingCard(null);
                            setIsFromProfile(false);
                            navigateTo('bankCardForm');
                        }}
                        onReadContract={(mode) => {
                            setSelectedMode(mode);
                            setIsFromConfirmOrder(true);
                            navigateTo('contractRead');
                        }}
                        purchaseContractAgreed={purchaseContractAgreed}
                        serviceContractAgreed={serviceContractAgreed}
                        onSubmit={() => {
                            if (!hasFaceVerified) {
                                setPendingFaceVerifyCallback('confirmOrder');
                                navigateTo('faceVerify');
                            } else {
                                // 创建新订单
                                const newOrderId = Date.now();
                                const newOrderNo = `ORD${new Date().toISOString().slice(0, 10).replace(/-/g, '')}${String(MOCK_ORDERS.length + 1).padStart(3, '0')}`;
                                const estimatedAmount = orderData?.weight ? parseFloat(orderData.weight) * 2650 : 0;

                                const newOrder = {
                                    id: newOrderId,
                                    orderNo: newOrderNo,
                                    status: '进行中',
                                    mode: orderData?.mode || 'self',
                                    baseName: orderData?.base?.name || '马钢诚兴马鞍山基地',
                                    baseAddress: orderData?.base?.address || '安徽省马鞍山市雨山区',
                                    material: orderData?.material || '重型废钢 (HMS1)',
                                    estimatedWeight: parseFloat(orderData?.weight) || 0,
                                    estimatedAmount: estimatedAmount,
                                    actualWeight: 0,
                                    actualAmount: 0,
                                    deliveryDeadline: orderData?.deliveryDeadline || new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
                                    supplierInfo: null,
                                    supplierConfirmed: true,
                                    createdAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
                                    invoiced: false,
                                    progress: { total: 0, waiting: 0, arrived: 0, stored: 0, needConfirm: 0, settled: 0, invoiced: 0, paid: 0 },
                                    deliveries: []
                                };

                                // 添加到订单列表
                                MOCK_ORDERS.unshift(newOrder);

                                showToast("订单创建成功！合同已自动生成");
                                setSelectedOrder(newOrder);
                                setOrderData(null);
                                setView('orderDetail');
                            }
                        }}
                    />
                )}
                {view === 'bankCardList' && (
                    <BankCardListPage
                        bankCards={bankCards}
                        selectedCardId={selectedCardId}
                        onBack={() => {
                            setIsFromProfile(false);
                            setView(isFromProfile ? 'profile' : 'confirmOrder');
                        }}
                        onSelect={(cardId) => {
                            setSelectedCardId(cardId);
                            setView(isFromProfile ? 'profile' : 'confirmOrder');
                        }}
                        onAdd={() => {
                            setEditingCard(null);
                            navigateTo('bankCardForm');
                        }}
                        onEdit={(card) => {
                            setEditingCard(card);
                            navigateTo('bankCardForm');
                        }}
                        onDelete={(cardId) => {
                            setBankCards(prev => prev.filter(card => card.id !== cardId));
                            if (selectedCardId === cardId) {
                                setSelectedCardId(null);
                            }
                            showToast("银行卡已删除");
                        }}
                    />
                )}
                {view === 'bankCardForm' && (
                    <BankCardFormPage
                        editingCard={editingCard}
                        onBack={() => setView(isFromProfile ? 'bankCardList' : 'confirmOrder')}
                        onSave={(cardData) => {
                            if (editingCard) {
                                setBankCards(prev => prev.map(card => 
                                    card.id === editingCard.id ? cardData : card
                                ));
                                showToast("银行卡已更新");
                            } else {
                                setBankCards(prev => [...prev, cardData]);
                                setSelectedCardId(cardData.id);
                                showToast("银行卡已添加");
                            }
                            setView(isFromProfile ? 'bankCardList' : 'confirmOrder');
                        }}
                    />
                )}
                {view === 'contractRead' && (
                    <ContractReadPage
                        mode={selectedMode}
                        onBack={() => {
                            if (isFromConfirmOrder) {
                                setIsFromConfirmOrder(false);
                                navigateTo('confirmOrder', orderData);
                            } else {
                                setView('createOrder');
                            }
                        }}
                        onAgree={() => {
                            if (selectedMode === 'other') {
                                setServiceContractAgreed(true);
                            } else {
                                setPurchaseContractAgreed(true);
                                setServiceContractAgreed(true);
                            }
                            if (isFromConfirmOrder) {
                                setIsFromConfirmOrder(false);
                                navigateTo('confirmOrder', orderData);
                            } else {
                                setView('createOrder');
                            }
                        }}
                    />
                )}
                {view === 'supplierContractRead' && (
                    <ContractReadPage
                        mode="other"
                        isFromSupplierConfirm={isFromSupplierConfirm}
                        onBack={() => {
                            const shouldReturnToSupplier = isFromSupplierConfirm;
                            setIsFromSupplierConfirm(false);
                            if (shouldReturnToSupplier) {
                                setView('supplierConfirm');
                            } else {
                                setView('createOrder');
                            }
                        }}
                        onAgree={() => {
                            const shouldReturnToSupplier = isFromSupplierConfirm;
                            setSupplierPurchaseContractAgreed(true);
                            setSupplierServiceContractAgreed(true);
                            setIsFromSupplierConfirm(false);
                            if (shouldReturnToSupplier) {
                                setView('supplierConfirm');
                            } else {
                                setView('createOrder');
                            }
                        }}
                    />
                )}
                {view === 'organizerContractRead' && (
                    <ContractReadPage
                        mode="other"
                        contractType={currentContractType}
                        isFromSupplierConfirm={false}
                        onBack={() => {
                            setCurrentContractType(null);
                            setView('createOrder');
                        }}
                        onAgree={() => {
                            if (currentContractType === 'service') {
                                setServiceContractAgreed(true);
                            }
                            setCurrentContractType(null);
                            setView('createOrder');
                        }}
                    />
                )}
            </div>

            {/* 底部导航栏 */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 h-16 flex items-center justify-around z-30 max-w-md mx-auto">
                <NavItem 
                    icon={Home} 
                    label="首页" 
                    active={view === 'home'} 
                    onClick={() => setView('home')} 
                />
                <NavItem 
                    icon={FileText} 
                    label="订单" 
                    active={view === 'orders'} 
                    onClick={() => setView('orders')} 
                />
                <NavItem 
                    icon={TrendingUp} 
                    label="发现" 
                    active={view === 'discover'} 
                    onClick={() => setView('discover')} 
                />
                <NavItem 
                    icon={User} 
                    label="我的" 
                    active={view === 'profile'} 
                    onClick={() => setView('profile')} 
                />
            </div>
        </div>
    );
}
